import React, { useState, useEffect, useMemo } from "react";
import ReactSelect from "react-select";
import { 
  listCitasActivas, 
  listHorariosGenerales, 
  listConsultorios,
  updateCita,
  listMedicos,
  listConfiguracion
} from "../api/odontologia";
import { listUsuarios } from "../api/usuarios";
import Loader from "./Loader";
import { 
  FiCalendar, FiChevronLeft, FiChevronRight, 
  FiClock, FiUser, FiActivity, FiLayers, FiCheckCircle,
  FiMapPin, FiInfo, FiHash, FiDollarSign, FiTrash2, FiX,
  FiBriefcase, FiAlertCircle, FiSave, FiEdit3, FiGrid
} from "react-icons/fi";

const CalendarioCitasOdontologia = () => {
  const [loading, setLoading] = useState(true);
  
  const formatDateLocal = (f) => {
    if (!f) return "N/A";
    const [y, m, d] = f.split("T")[0].split("-").map(Number);
    const date = new Date(y, m - 1, d);
    return date.toLocaleDateString('es-ES', { dateStyle: 'long' });
  };
  const [citas, setCitas] = useState([]);
  const [horariosBase, setHorariosBase] = useState([]);
  const [consultorios, setConsultorios] = useState([]);
  const [doctores, setDoctores] = useState([]);
  const [config, setConfig] = useState({
    calendarios_cantidad: 1,
    tiempo_calendarios: 30
  });
  
  const [view, setView] = useState("semana"); // "semana" o "dia"
  const [currentDate, setCurrentDate] = useState(new Date());
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedCita, setSelectedCita] = useState(null);
  const [updating, setUpdating] = useState(false);
  
  const [formEdit, setFormEdit] = useState({
    estado: "",
    id_consultorio: "",
    id_usuario_asignado: ""
  });

  const fetchData = async () => {
    setLoading(true);
    try {
      const [resCitas, resHorarios, resConsul, resDocs, resConfig] = await Promise.all([
        listCitasActivas(),
        listHorariosGenerales(),
        listConsultorios(),
        listMedicos(),
        listConfiguracion()
      ]);
      setCitas(resCitas?.data || []);
      setHorariosBase(resHorarios?.data || []);
      setConsultorios(resConsul?.data || []);
      setDoctores(resDocs?.data || []);
      if (resConfig && resConfig.estado === 1 && resConfig.data) {
        setConfig({
          calendarios_cantidad: resConfig.data.calendarios_cantidad || 1,
          tiempo_calendarios: resConfig.data.tiempo_calendarios || 30
        });
      }
    } catch (error) {
      console.error("Error al cargar datos del calendario:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const safeParseJSON = (data) => {
    if (!data) return [];
    try {
      let parsed = typeof data === "string" ? JSON.parse(data) : data;
      if (typeof parsed === "string") {
        parsed = JSON.parse(parsed);
      }
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      console.error("SafeParse Error:", e);
      return [];
    }
  };

  // Helper para procesar el JSON de la cita y calcular duración
  const processCita = (cita) => {
    const procedures = safeParseJSON(cita.json);
    let totalMinutos = 0;
      
    if (procedures.length > 0) {
      totalMinutos = procedures.reduce((acc, curr) => {
        const duracion = parseInt(curr.duracion_minutos) || 0;
        const cantidad = parseInt(curr.cantidad) || 1;
        return acc + (duracion * cantidad);
      }, 0);
    }

    // Fallback: Si no hay duración definida en los procedimientos, intentar usar la hora_fin de la base de datos
    if (totalMinutos === 0 && cita.hora_fin && cita.hora_inicio) {
      try {
        const [h1, m1] = cita.hora_inicio.split(":").map(Number);
        const [h2, m2] = cita.hora_fin.split(":").map(Number);
        const d1 = new Date(2000, 0, 1, h1, m1);
        const d2 = new Date(2000, 0, 1, h2, m2);
        totalMinutos = (d2.getTime() - d1.getTime()) / 60000;
      } catch (e) {
        console.error("Error parseando hora_fin fallback:", e);
      }
    }
    
    // Mínimo 30 min si sigue siendo 0 o negativo
    if (totalMinutos <= 0) totalMinutos = 30;

    // Calcular hora fin calculada para visualización uniforme
    const [h, m] = cita.hora_inicio.split(":").map(Number);
    const startDate = new Date(2000, 0, 1, h, m);
    const endDate = new Date(startDate.getTime() + totalMinutos * 60000);
    
    return {
      ...cita,
      procedures,
      duracionTotal: totalMinutos,
      hora_fin_calc: `${endDate.getHours().toString().padStart(2, '0')}:${endDate.getMinutes().toString().padStart(2, '0')}:00`
    };
  };

  const citasProcesadas = useMemo(() => citas.map(processCita), [citas]);

  // Helpers de fechas
  const getWeekDays = (date) => {
    const startOfWeek = new Date(date);
    startOfWeek.setHours(0, 0, 0, 0);
    const day = startOfWeek.getDay();
    const diff = startOfWeek.getDate() - day + (day === 0 ? -6 : 1); // Lunes es el primer día
    startOfWeek.setDate(diff);
    
    const days = [];
    for (let i = 0; i < 7; i++) {
        const nextDay = new Date(startOfWeek);
        nextDay.setDate(startOfWeek.getDate() + i);
        days.push(nextDay);
    }
    return days;
  };

  const weekDays = useMemo(() => getWeekDays(currentDate), [currentDate]);

  const navigateDate = (amount) => {
    const newDate = new Date(currentDate);
    if (view === "semana") {
        newDate.setDate(currentDate.getDate() + (amount * 7));
    } else {
        newDate.setDate(currentDate.getDate() + amount);
    }
    setCurrentDate(newDate);
  };

  const { minHour, maxHour } = useMemo(() => {
    if (horariosBase.length === 0) return { minHour: 8, maxHour: 19 };
    const activeData = horariosBase.filter(h => h.status === 1);
    if (activeData.length === 0) return { minHour: 8, maxHour: 19 };

    const hInicios = activeData.map(h => parseInt(h.hora_inicio.split(':')[0]));
    const hFines = activeData.map(h => {
        const [hh, mm] = h.hora_fin.split(':').map(Number);
        // Si termina exactamente en :00, no necesitamos ese slot de la hora siguiente
        return mm === 0 ? hh : hh + 1;
    });

    return {
      minHour: Math.min(...hInicios),
      maxHour: Math.max(...hFines)
    };
  }, [horariosBase]);

  const timeSlots = [];
  for (let i = minHour; i < maxHour; i++) {
    timeSlots.push(`${i.toString().padStart(2, '0')}:00`);
    timeSlots.push(`${i.toString().padStart(2, '0')}:30`);
  }
  // Añadir la última línea de cierre si es necesario
  timeSlots.push(`${maxHour.toString().padStart(2, '0')}:00`);

  const getDayWorkingStatus = (day, time) => {
    const diaSemana = day.getDay() === 0 ? 7 : day.getDay();
    const rangos = horariosBase.filter(h => h.dia_semana === diaSemana && h.status === 1);
    if (!rangos.length) return false;

    const [h, m] = time.split(":").map(Number);
    const timeVal = `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:00`;
    
    return rangos.some(r => timeVal >= r.hora_inicio && timeVal < r.hora_fin);
  };

  const getDayRangeString = (day) => {
    const diaSemana = day.getDay() === 0 ? 7 : day.getDay();
    const rangos = horariosBase.filter(h => h.dia_semana === diaSemana && h.status === 1);
    if (!rangos.length) return "Cerrado";
    
    const minStart = rangos.sort((a,b) => a.hora_inicio.localeCompare(b.hora_inicio))[0].hora_inicio.substring(0,5);
    const maxEnd = rangos.sort((a,b) => b.hora_fin.localeCompare(a.hora_fin))[0].hora_fin.substring(0,5);
    return `${minStart} - ${maxEnd}`;
  };

  const slotHeight = 40; // Pixeles por cada 30 minutos

  const getPositionStyles = (cita) => {
    const [h, m] = cita.hora_inicio.split(":").map(Number);
    const top = (h - minHour) * (slotHeight * 2) + (m / 30) * slotHeight;
    const height = (cita.duracionTotal / 30) * slotHeight;
    
    const totalCols = config.calendarios_cantidad || 1;
    const colWidth = 100 / totalCols;
    const left = ((cita.calendario || 1) - 1) * colWidth;

    return { 
      top: `${top}px`, 
      height: `${height}px`,
      left: `${left}%`,
      width: `${colWidth}%`,
      padding: '0 2px' // Espaciado entre columnas
    };
  };

  const getColorByTipo = (tipo) => {
    switch (tipo?.toLowerCase()) {
      case "evaluacion": return { bg: "bg-amber-50", border: "border-amber-400", text: "text-amber-700", accent: "bg-amber-400" };
      case "procedimiento": return { bg: "bg-indigo-50", border: "border-indigo-500", text: "text-indigo-700", accent: "bg-indigo-500" };
      case "control": return { bg: "bg-teal-50", border: "border-teal-400", text: "text-teal-700", accent: "bg-teal-400" };
      default: return { bg: "bg-slate-50", border: "border-slate-400", text: "text-slate-700", accent: "bg-slate-400" };
    }
  };

  if (loading) return <Loader />;

  return (
    <div className="flex flex-col h-full bg-slate-50 overflow-hidden font-sans animation-fade-in">
      
      {/* HEADER */}
      <div className="bg-white/80 backdrop-blur-md border-b border-slate-200 px-6 py-4 flex flex-col sm:flex-row items-center justify-between gap-4 shadow-sm shrink-0 z-30">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-indigo-600 to-violet-600 flex items-center justify-center text-white shadow-xl shadow-indigo-100">
            <FiCalendar size={24} />
          </div>
          <div>
            <h2 className="text-xl font-black text-slate-800 leading-tight tracking-tight">Agenda Odontológica</h2>
            <div className="flex items-center gap-2 text-[10px] font-black text-slate-400 uppercase tracking-[0.15em] mt-0.5">
               <span className={view === "semana" ? "text-indigo-600" : ""}>{view === "semana" ? "Semanal" : "Diaria"}</span>
               <span className="w-1 h-1 rounded-full bg-slate-300"></span>
               <span className="text-slate-500">
                  {weekDays[0].toLocaleDateString('es-ES', { day: '2-digit', month: 'short' })} - {weekDays[6].toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' })}
               </span>
            </div>
          </div>
        </div>

        <div className="flex items-center bg-slate-100 p-1.5 rounded-2xl border border-slate-200">
          <button 
            onClick={() => setView("semana")}
            className={`px-5 py-2 rounded-xl text-xs font-black uppercase tracking-widest transition-all duration-300 ${view === "semana" ? "bg-white text-indigo-600 shadow-lg shadow-indigo-100 scale-[1.02]" : "text-slate-500 hover:text-slate-700"}`}
          >
            Semana
          </button>
          <button 
            onClick={() => setView("dia")}
            className={`px-5 py-2 rounded-xl text-xs font-black uppercase tracking-widest transition-all duration-300 ${view === "dia" ? "bg-white text-indigo-600 shadow-lg shadow-indigo-100 scale-[1.02]" : "text-slate-500 hover:text-slate-700"}`}
          >
            Día
          </button>
        </div>

        <div className="flex items-center gap-2">
           <button onClick={() => navigateDate(-1)} className="p-3 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 text-slate-600 transition-all active:scale-90 hover:shadow-md"><FiChevronLeft size={20} /></button>
           <button onClick={() => setCurrentDate(new Date())} className="px-5 py-2.5 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 text-xs font-black text-slate-700 uppercase tracking-widest transition-all hover:shadow-md">Hoy</button>
           <button onClick={() => navigateDate(1)} className="p-3 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 text-slate-600 transition-all active:scale-90 hover:shadow-md"><FiChevronRight size={20} /></button>
        </div>
      </div>

      {/* CALENDAR BODY */}
      <div className="flex-1 overflow-auto p-4 sm:p-6 custom-scrollbar">
        <div className="min-w-[1000px] h-full flex flex-col bg-white rounded-[2.5rem] shadow-2xl shadow-slate-200/50 border border-slate-100 overflow-hidden relative">
          
          {/* Days Header */}
          <div className="grid grid-cols-[110px_repeat(7,1fr)] border-b border-slate-100 bg-slate-50/80 backdrop-blur-sm sticky top-0 z-20 shadow-sm">
            <div className="p-5 flex items-center justify-center border-r border-slate-100">
               <FiClock className="text-slate-300" size={20} />
            </div>
            {weekDays.map((day, idx) => {
              const isToday = new Date().toDateString() === day.toDateString();
              const isSelected = view === "dia" && day.toDateString() === currentDate.toDateString();
              
              if (view === "dia" && !isSelected) return null;

              return (
                <div 
                  key={idx} 
                  className={`p-4 text-center border-r border-slate-100 last:border-0 ${view === "dia" ? "col-span-1" : ""} ${isSelected || isToday ? "bg-gradient-to-b from-indigo-50/50 to-transparent" : ""}`}
                  style={view === "dia" ? { gridColumn: "2 / span 7" } : {}}
                >
                  <p className="text-[10px] font-black text-slate-400 uppercase tracking-[0.25em] mb-1">{day.toLocaleDateString('es-ES', { weekday: 'short' })}</p>
                  <div className={`inline-flex items-center justify-center w-9 h-9 rounded-full text-lg font-black transition-all mb-1.5 ${isToday ? "bg-indigo-600 text-white shadow-lg shadow-indigo-100" : "text-slate-800"}`}>
                    {day.getDate()}
                  </div>
                  <div className="flex items-center justify-center gap-1.5 text-[8px] font-black text-indigo-500 uppercase tracking-widest bg-indigo-50/50 py-1 rounded-lg border border-indigo-100/50">
                     <FiClock size={10} />
                     {getDayRangeString(day)}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Grid Content */}
          <div className="relative flex-1 overflow-y-auto custom-scrollbar" style={{ height: `${timeSlots.length * slotHeight}px` }}>
            
            {/* Background Time Lines */}
            {timeSlots.map((time, idx) => (
              <div 
                key={idx} 
                className="grid grid-cols-[110px_repeat(7,1fr)] border-b border-slate-50 group/row transition-colors"
                style={{ height: `${slotHeight}px` }}
              >
                <div className="text-[10px] font-black text-slate-400 flex items-center justify-center border-r border-slate-100 bg-slate-50/50">
                  {time}
                </div>
                {weekDays.map((day, i) => {
                   const isSelected = view === "dia" && day.toDateString() === currentDate.toDateString();
                   if (view === "dia" && !isSelected) return null;
                   
                   const isWorking = getDayWorkingStatus(day, time);
                   const isFirstSlot = time === `${minHour.toString().padStart(2, '0')}:00`;
                   
                   return (
                      <div 
                         key={i} 
                         className={`border-r border-slate-50 last:border-0 transition-all duration-300 relative
                            ${!isWorking ? "bg-slate-100/80 shade-diagonal" : "bg-white group-hover/row:bg-indigo-50/20"}`}
                      >
                         {isWorking && (
                            <div className="absolute inset-x-0.5 border-l-2 border-indigo-400/30 h-full pointer-events-none"></div>
                         )}
                      </div>
                   );
                })}
              </div>
            ))}

            {/* APPOINTMENTS LAYER */}
            <div className="absolute inset-0 pointer-events-none" style={{ left: '110px' }}>
              <div className={`grid h-full ${view === "dia" ? "grid-cols-1" : "grid-cols-7"}`}>
                
                {weekDays.map((day, dayIdx) => {
                  const isDaySelected = day.toDateString() === currentDate.toDateString();
                  if (view === "dia" && !isDaySelected) return null;

                  const dayStr = `${day.getFullYear()}-${String(day.getMonth() + 1).padStart(2, '0')}-${String(day.getDate()).padStart(2, '0')}`;
                  const dayCitas = citasProcesadas.filter(c => c.fecha === dayStr);
                  
                  // Horarios base para este día
                  const diaSemana = day.getDay() === 0 ? 7 : day.getDay();
                  const esHorarioLaboral = horariosBase.some(h => h.dia_semana === diaSemana && h.status === 1);

                  return (
                    <div key={dayIdx} className="relative h-full border-r border-slate-100/50 last:border-0">
                      
                      {dayCitas.length === 0 && view === "dia" && (
                        <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                           <div className="w-20 h-20 rounded-full bg-slate-50 flex items-center justify-center mb-4 border border-slate-100 shadow-inner">
                              <FiCalendar className="text-slate-200" size={32} />
                           </div>
                           <p className="text-xs font-black text-slate-300 uppercase tracking-[0.2em]">No hay citas agendadas</p>
                           <p className="text-[10px] text-slate-400 mt-1 italic">Este día está completamente libre</p>
                        </div>
                      )}
                      
                      {!esHorarioLaboral && (
                        <div className="absolute inset-0 bg-slate-50/40 pattern-dots opacity-20 pointer-events-none"></div>
                      )}

                      {dayCitas.map(cita => {
                        const styles = getPositionStyles(cita);
                        const colors = getColorByTipo(cita.tipo_cita);
                        return (
                          <div 
                            key={cita.id}
                            className="absolute pointer-events-auto group/card"
                            style={styles}
                             onClick={() => {
                               setSelectedCita(cita);
                               setFormEdit({
                                 ...cita,
                                 id_consultorio: cita.id_consultorio?.toString() || "",
                                 id_medico: cita.id_medico?.toString() || ""
                               });
                               setShowDetailModal(true);
                             }}
                          >
                            <div 
                              className={`h-full ${colors.bg} backdrop-blur-md bg-opacity-80 rounded-2xl border ${colors.border} shadow-lg flex flex-col overflow-hidden transition-all duration-300 hover:shadow-2xl hover:scale-[1.03] hover:z-20 cursor-pointer 
                              ${cita.duracionTotal <= 30 ? "p-1.5 sm:p-2" : "p-2.5 sm:p-3"}`}
                              title={`${cita.ClientesReceptor?.nombre} (${cita.hora_inicio.substring(0,5)} - ${cita.hora_fin_calc.substring(0,5)})`}
                            >
                                <div className={`absolute top-0 left-0 w-1.5 h-full ${colors.accent} shadow-[0_0_10px_rgba(0,0,0,0.1)]`}></div>
                                
                                <div className={`flex flex-col min-w-0 ${cita.duracionTotal <= 30 ? "gap-0" : "gap-1"}`}>
                                  <span className={`${cita.duracionTotal <= 30 ? "text-[9px]" : "text-[10px] sm:text-[11px]"} font-black text-slate-800 truncate uppercase tracking-tighter block leading-tight`}>
                                     {cita.ClientesReceptor?.nombre}
                                  </span>
                                  
                                  <div className={`flex items-center gap-1 text-slate-600 font-bold italic ${cita.duracionTotal <= 30 ? "text-[7px]" : "text-[8px] sm:text-[9px]"}`}>
                                     <FiClock size={cita.duracionTotal <= 30 ? 8 : 10} className={colors.text} />
                                     {cita.hora_inicio.substring(0,5)} - {cita.hora_fin_calc.substring(0,5)}
                                  </div>
                                </div>
                                
                                {cita.duracionTotal >= 45 && (
                                  <div className="mt-auto pt-2 flex flex-wrap gap-1.5">
                                    {cita.procedures.slice(0, 2).map((s, i) => (
                                      <span key={i} className="text-[7px] font-black text-slate-500 bg-white/60 px-1.5 py-0.5 rounded-md uppercase border border-slate-200">
                                        {s.name}
                                      </span>
                                    ))}
                                    {cita.procedures.length > 2 && <span className="text-[8px] font-black text-slate-400">+{cita.procedures.length - 2}</span>}
                                  </div>
                                )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            </div>

          </div>
        </div>
      </div>

      {/* FOOTER LEGEND */}
      <div className="bg-white border-t border-slate-200 px-8 py-3.5 flex items-center justify-between shrink-0">
         <div className="flex items-center gap-8">
            <div className="flex items-center gap-2.5">
               <div className="w-4 h-4 rounded-lg bg-indigo-500 shadow-inner"></div>
               <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Procedimiento</span>
            </div>
            <div className="flex items-center gap-2.5">
               <div className="w-4 h-4 rounded-lg bg-amber-400 shadow-inner"></div>
               <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Evaluación</span>
            </div>
            <div className="flex items-center gap-2.5">
               <div className="w-4 h-4 rounded-lg bg-teal-400 shadow-inner"></div>
               <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Control</span>
            </div>
         </div>
         
         <div className="flex items-center gap-2 text-[10px] font-black text-slate-400 uppercase tracking-widest italic">
            <FiInfo size={14} className="text-slate-300" />
            <span>Haz clic en una cita para ver detalles</span>
         </div>
      </div>

      {/* DETALLE MODAL */}
      {showDetailModal && selectedCita && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-md flex justify-center items-center z-[100] p-4 animation-fade-in">
          <div className="bg-white rounded-[2.5rem] shadow-2xl w-full max-w-xl max-h-[95vh] flex flex-col overflow-hidden border border-slate-200 animation-slide-up">
            
            {/* Modal Header */}
            <div className={`p-6 sm:p-8 ${getColorByTipo(selectedCita.tipo_cita).bg} relative overflow-hidden flex flex-col shrink-0`}>
                <button 
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowDetailModal(false);
                  }}
                  className="absolute top-6 right-6 w-12 h-12 rounded-2xl bg-white/60 hover:bg-white text-slate-800 transition-all flex items-center justify-center shadow-lg z-[110] hover:scale-110 active:scale-90"
                >
                  <FiX size={24} />
                </button>

                <div className="flex items-center gap-4 mb-6 relative z-10">
                   <div className="w-16 h-16 rounded-2xl bg-white flex items-center justify-center text-indigo-600 shadow-xl">
                      <FiUser size={32} />
                   </div>
                   <div>
                      <h4 className="text-2xl font-black text-slate-800 tracking-tight leading-none mb-1">{selectedCita.ClientesReceptor?.nombre}</h4>
                   </div>
                </div>

                <div className="flex flex-wrap gap-2 relative z-10">
                   <span className="bg-white/80 backdrop-blur-sm text-slate-700 px-4 py-1.5 rounded-full text-xs font-black uppercase tracking-widest border border-white/50">
                      {selectedCita.tipo_cita}
                   </span>
                   <span className="bg-indigo-600 text-white px-4 py-1.5 rounded-full text-xs font-black uppercase tracking-widest shadow-lg shadow-indigo-100">
                      {selectedCita.estado}
                   </span>
                </div>
            </div>

            {/* Modal Content - Scrollable area */}
            <div className="p-6 sm:p-8 overflow-y-auto custom-scrollbar flex-1">
               <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 sm:gap-8 mb-8">
                  <div className="flex items-start gap-3">
                     <div className="p-2.5 rounded-xl bg-slate-50 text-slate-400">
                        <FiCalendar size={18} />
                     </div>
                     <div>
                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Fecha</p>
                        <p className="text-sm font-black text-slate-700">{formatDateLocal(selectedCita.fecha)}</p>
                     </div>
                  </div>
                  <div className="flex items-start gap-3">
                     <div className="p-2.5 rounded-xl bg-slate-50 text-slate-400">
                        <FiClock size={18} />
                     </div>
                     <div>
                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Horario</p>
                        <p className="text-sm font-black text-slate-700">{selectedCita.hora_inicio.substring(0,5)} - {selectedCita.hora_fin_calc.substring(0,5)}</p>
                     </div>
                  </div>
                  <div className="flex items-start gap-3">
                     <div className="p-2.5 rounded-xl bg-slate-50 text-slate-400">
                        <FiUser size={18} />
                     </div>
                     <div>
                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Médico / Profesional</p>
                        <p className="text-sm font-black text-slate-700">
                           {doctores.find(d => Number(d.id) === Number(selectedCita.id_medico))?.Usuario 
                             ? `${doctores.find(d => Number(d.id) === Number(selectedCita.id_medico)).Usuario.nombre} ${doctores.find(d => Number(d.id) === Number(selectedCita.id_medico)).Usuario.apellido || ""}` 
                             : "Sin asignar"}
                        </p>
                     </div>
                  </div>
                  <div className="flex items-start gap-3">
                     <div className="p-2.5 rounded-xl bg-slate-50 text-slate-400">
                        <FiMapPin size={18} />
                     </div>
                     <div>
                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Consultorio</p>
                        <p className="text-sm font-black text-slate-700">
                           {consultorios.find(c => String(c.id) === String(selectedCita.id_consultorio))?.nombre || "Canal Principal"}
                        </p>
                     </div>
                  </div>
                  <div className="flex items-start gap-3">
                     <div className="p-2.5 rounded-xl bg-slate-50 text-slate-400">
                        <FiGrid size={18} />
                     </div>
                     <div>
                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Espacio de Agenda</p>
                        <p className="text-sm font-black text-indigo-600 uppercase tracking-tighter">Espacio #{selectedCita.calendario || 1}</p>
                     </div>
                  </div>
               </div>

               <div className="mb-8 p-6 rounded-3xl bg-slate-50/50 border border-slate-100">
                  <h5 className="text-xs font-black text-slate-800 uppercase tracking-[0.2em] mb-4 flex items-center gap-2">
                     <FiLayers className="text-indigo-500" /> Procedimientos Planificados
                  </h5>
                  <div className="space-y-3">
                     {selectedCita.procedures.map((s, i) => (
                        <div key={i} className="flex items-center justify-between p-3 bg-white rounded-2xl border border-slate-100 shadow-sm transition-all hover:border-indigo-200">
                           <div className="flex items-center gap-3">
                              <span className="w-8 h-8 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center font-black text-xs">{s.cantidad}</span>
                              <span className="text-sm font-bold text-slate-700">{s.name}</span>
                           </div>
                           <span className="text-xs font-black text-slate-400 font-mono">${(parseFloat(s.price) * s.cantidad).toFixed(2)}</span>
                        </div>
                     ))}
                     {selectedCita.procedures.length === 0 && <p className="text-xs text-slate-400 italic">No hay procedimientos especificados.</p>}
                  </div>
               </div>

               {selectedCita.observaciones && (
                  <div className="p-6 rounded-3xl bg-indigo-50/30 border border-indigo-100/50 mb-8">
                     <h5 className="text-xs font-black text-indigo-700 uppercase tracking-[0.2em] mb-2 flex items-center gap-2">
                        <FiActivity /> Observaciones
                     </h5>
                     <p className="text-sm text-slate-600 font-medium leading-relaxed">{selectedCita.observaciones}</p>
                  </div>
                )}

                {/* EDIT SECTION */}
                <div className="p-6 rounded-3xl bg-slate-50 border border-slate-200">
                   <h5 className="text-xs font-black text-slate-800 uppercase tracking-[0.2em] mb-6 flex items-center gap-2">
                      <FiEdit3 className="text-indigo-500" /> Gestión de la Cita
                   </h5>
                   <div className="grid grid-cols-1 gap-5">
                      <div>
                         <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1.5 ml-1">Estado de la Cita</label>
                         <ReactSelect 
                            options={[
                               { value: "Programada", label: "Programada" },
                               { value: "Confirmada", label: "Confirmada" },
                               { value: "Finalizada", label: "Finalizada" },
                               { value: "Cancelada", label: "Cancelada" }
                            ]}
                            value={{ value: formEdit.estado, label: formEdit.estado }}
                            onChange={(val) => setFormEdit(prev => ({ ...prev, estado: val.value }))}
                            className="text-sm"
                            menuPortalTarget={document.body}
                            styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                         />
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                         <div>
                            <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1.5 ml-1">Médico / Profesional</label>
                            <ReactSelect 
                               options={doctores.filter(d => String(d.status) === "1").map(d => ({ value: d.id.toString(), label: `${d.Usuario?.nombre || ""} ${d.Usuario?.apellido || ""}` }))}
                               value={doctores.find(d => d.id.toString() === formEdit.id_medico) ? { value: formEdit.id_medico, label: `${doctores.find(d => d.id.toString() === formEdit.id_medico).Usuario?.nombre || ""} ${doctores.find(d => d.id.toString() === formEdit.id_medico).Usuario?.apellido || ""}` } : null}
                               onChange={(val) => setFormEdit(prev => ({ ...prev, id_medico: val ? val.value : "" }))}
                               placeholder="Seleccione Médico..."
                               className="text-sm"
                               isClearable
                               menuPortalTarget={document.body}
                               styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                            />
                         </div>
                         <div>
                            <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1.5 ml-1">Asignar Consultorio</label>
                            <ReactSelect 
                               options={consultorios.map(c => ({ value: c.id.toString(), label: c.nombre }))}
                               value={consultorios.find(c => c.id.toString() === formEdit.id_consultorio) ? { value: formEdit.id_consultorio, label: consultorios.find(c => c.id.toString() === formEdit.id_consultorio).nombre } : null}
                               onChange={(val) => setFormEdit(prev => ({ ...prev, id_consultorio: val ? val.value : "" }))}
                               placeholder="Seleccione Consultorio..."
                               className="text-sm"
                               isClearable
                               menuPortalTarget={document.body}
                               styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                            />
                         </div>
                      </div>
                   </div>
                </div>
            </div>

            <div className="p-6 sm:p-8 border-t border-slate-100 flex justify-end gap-3 shrink-0">
               <button 
                onClick={() => setShowDetailModal(false)}
                className="px-6 py-3 rounded-2xl border border-slate-200 text-slate-500 text-xs font-black uppercase tracking-widest hover:bg-slate-50 transition-all active:scale-95"
               >
                 Cerrar
               </button>
               <button 
                onClick={async () => {
                   setUpdating(true);
                   try {
                      const dataToSend = {
                         ...formEdit,
                         id_medico: formEdit.id_medico ? parseInt(formEdit.id_medico) : null,
                         id_consultorio: formEdit.id_consultorio ? parseInt(formEdit.id_consultorio) : null,
                         id_tratamiento: formEdit.id_tratamiento ? parseInt(formEdit.id_tratamiento) : null,
                      };
                      await updateCita(selectedCita.id, dataToSend);
                      setShowDetailModal(false);
                      fetchData();
                   } catch (e) {
                      console.error("Error al actualizar cita:", e);
                   } finally {
                      setUpdating(false);
                   }
                }}
                disabled={updating}
                className="px-8 py-3 rounded-2xl bg-indigo-600 text-white text-xs font-black uppercase tracking-widest hover:bg-indigo-700 transition-all hover:shadow-xl active:scale-95 flex items-center justify-center gap-2"
               >
                 {updating ? <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span> : <FiSave size={16} />}
                 Guardar Cambios
               </button>
            </div>

          </div>
        </div>
      )}

      <style>{`
        .shade-diagonal {
          background-image: repeating-linear-gradient(
            -45deg,
            #f1f5f9,
            #f1f5f9 8px,
            #e2e8f0 8px,
            #e2e8f0 16px
          );
          opacity: 0.6;
        }
        .pattern-diagonal-lines {
          background: repeating-linear-gradient(
            -45deg,
            rgba(241, 245, 249, 1),
            rgba(241, 245, 249, 1) 10px,
            rgba(226, 232, 240, 0.4) 10px,
            rgba(226, 232, 240, 0.4) 20px
          );
        }
        .pattern-dots {
          background-image: radial-gradient(#94a3b8 0.5px, transparent 0.5px);
          background-size: 8px 8px;
        }
        .custom-scrollbar::-webkit-scrollbar {
          width: 6px;
          height: 6px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: transparent;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #e2e8f0;
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #cbd5e1;
        }
        .animation-fade-in {
          animation: fadeIn 0.4s ease-out forwards;
        }
        .animation-slide-up {
          animation: slideUp 0.5s cubic-bezier(0.16, 1, 0.3, 1) forwards;
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideUp {
          from { transform: translateY(30px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
      `}</style>

    </div>
  );
};

export default CalendarioCitasOdontologia;
